/**
 * AbilityCastBenchTab — modernized, high-fidelity ability visual lab bench.
 *
 * Provides target staging, visual ability browsing with authentic icons & badges,
 * rich ability stat inspection, instant free cast, and segmented hotbar binding.
 */
import React, { useState, useMemo } from 'react';
import { ABILITY_GROUNDS_TARGETS } from '../../../../shared/qa/AbilityGroundsLab';
import { getAbilityData } from '../../../../shared/content/AbilityDataAdapter';
import { hasAbilityIcon, abilityIconPath } from '../../../rendering/abilities/AbilityIconRegistry';
import { PlayerServices } from '../../../core/Services';
import { uiActionBridge } from '../../framework/uiActionBridge';
import { ItemSlot } from '../../components/ItemKit';
import { Button, Card, Badge } from '../../primitives';
import { SPACE } from '../../theme';
import {
  type AbilityOption,
  type LabState,
  selectGroundsTarget,
} from './types';
import { prepareGroundsView } from './AbilityGroundsLabPanel';
import { benchStyles as styles } from './benchStyles';

interface AbilityCastBenchTabProps {
  labState: LabState;
  setLabState: (state: LabState) => void;
  abilities: AbilityOption[];
  selectedAbility: string;
  setSelectedAbility: (id: string) => void;
  selectedTarget: string;
  setSelectedTarget: (id: string) => void;
  lastAction: string;
  setLastAction: (action: string) => void;
  resetLab: () => void;
  sweepRunning: boolean;
}

const HOTBAR_SLOTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];

export const AbilityCastBenchTab: React.FC<AbilityCastBenchTabProps> = ({
  labState,
  setLabState,
  abilities,
  selectedAbility,
  setSelectedAbility,
  selectedTarget,
  setSelectedTarget,
  lastAction,
  setLastAction,
  resetLab,
  sweepRunning,
}) => {
  const [filterQuery, setFilterQuery] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(1);

  const filteredAbilities = useMemo(() => {
    if (!filterQuery.trim()) return abilities;
    const q = filterQuery.toLowerCase();
    return abilities.filter(
      (a) => a.name.toLowerCase().includes(q) || a.id.toLowerCase().includes(q),
    );
  }, [abilities, filterQuery]);

  const target = async (targetId: string) => {
    setSelectedTarget(targetId);
    const definition = ABILITY_GROUNDS_TARGETS.find((candidate) => candidate.id === targetId);
    setLabState('preparing');
    setLastAction(`Staging isolated ${definition?.label ?? targetId} view…`);
    try {
      await prepareGroundsView(targetId);
      const selected = selectGroundsTarget(targetId);
      setLabState('ready');
      setLastAction(
        selected
          ? `Targeted ${definition?.label ?? targetId} dummy · ${definition?.distance ?? '?'} px · other dummies parked`
          : 'Targeting system is not ready yet.',
      );
    } catch (error) {
      setLabState('error');
      setLastAction(`Target staging failed · ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const cast = () => {
    if (labState !== 'ready' || !selectedAbility) return;
    const manager = PlayerServices.manager();
    if (!manager) {
      setLastAction('Player manager is not ready yet.');
      return;
    }
    selectGroundsTarget(selectedTarget);
    const accepted = manager.performAbility(selectedAbility, { source: 'ability-grounds-lab' });
    const ability = abilities.find((candidate) => candidate.id === selectedAbility);
    setLastAction(
      accepted
        ? `Cast accepted · ${ability?.name ?? selectedAbility} → ${selectedTarget}`
        : `Cast rejected locally · ${ability?.name ?? selectedAbility} · check range or resources`,
    );
  };

  const assignToHotbar = () => {
    if (!selectedAbility) return;
    try {
      uiActionBridge.dispatch('updateActionBar', {
        slotIndex: selectedSlot,
        abilityId: selectedAbility,
      });
      const ability = abilities.find((candidate) => candidate.id === selectedAbility);
      setLastAction(
        `Assigned ${ability?.name ?? selectedAbility} to hotbar slot ${selectedSlot === 10 ? '0' : selectedSlot}`,
      );
    } catch (err) {
      setLastAction(`Failed to assign hotbar slot: ${String(err)}`);
    }
  };

  const selectedAbilityData = abilities.find((a) => a.id === selectedAbility);
  const detailDef = selectedAbility ? getAbilityData(selectedAbility) : undefined;

  const targetTone = (t?: string): 'danger' | 'info' | 'success' | 'neutral' => {
    if (!t) return 'neutral';
    if (t.includes('enemy')) return 'danger';
    if (t.includes('aoe')) return 'info';
    if (t.includes('self') || t.includes('friendly')) return 'success';
    return 'neutral';
  };

  return (
    <div style={styles.container}>
      {/* Target Staging Toolbar Card */}
      <Card
        title="Authored Targets & Staging"
        subtitle="Deterministic ranges · unselected dummies parked behind spawn"
        icon="◎"
      >
        <div style={styles.targetRow}>
          {ABILITY_GROUNDS_TARGETS.map((definition) => {
            const isSelected = selectedTarget === definition.id;
            return (
              <button
                key={definition.id}
                type="button"
                style={{
                  ...styles.targetPill,
                  ...(isSelected ? styles.targetPillActive : {}),
                }}
                disabled={sweepRunning}
                onClick={() => { void target(definition.id); }}
                data-testid={`grounds-target-${definition.id}`}
                title={`${definition.label} target at ${definition.distance}px`}
              >
                <span style={styles.targetPillLabel}>{definition.label}</span>
                <span style={styles.targetPillDist}>{definition.distance}px</span>
              </button>
            );
          })}
        </div>
        <div style={{ marginTop: SPACE.xs }}>
          <Button
            size="xs"
            variant="ghost"
            fullWidth
            disabled={labState === 'preparing'}
            onClick={resetLab}
            data-testid="grounds-reset-lab"
          >
            {labState === 'preparing' ? 'Restoring baseline…' : '↻ Reset lab baseline'}
          </Button>
        </div>
      </Card>

      {/* Ability Cast Bench */}
      <Card
        title="Ability Cast Bench"
        subtitle={`${abilities.length} data-driven class abilities · free casts`}
        icon="✦"
      >
        {/* Search input bar */}
        <div style={styles.searchBar}>
          <span style={styles.searchIcon} aria-hidden="true">🔍</span>
          <input
            type="text"
            placeholder="Search abilities by name or id…"
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            style={styles.searchInput}
            data-testid="grounds-ability-search"
          />
          {filterQuery && (
            <button
              type="button"
              style={styles.clearSearchBtn}
              onClick={() => setFilterQuery('')}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        {/* Rich Ability List */}
        <div
          style={styles.abilityList}
          role="listbox"
          aria-label="Class abilities"
          data-testid="grounds-ability-select"
        >
          {filteredAbilities.map((ability) => {
            const isSelected = selectedAbility === ability.id;
            const hasIcon = hasAbilityIcon(ability.id);
            const iconUrl = hasIcon ? abilityIconPath(ability.id) : undefined;
            const abData = getAbilityData(ability.id);

            return (
              <div
                key={ability.id}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                style={{
                  ...styles.abilityRow,
                  ...(isSelected ? styles.abilityRowSelected : {}),
                }}
                onClick={() => setSelectedAbility(ability.id)}
                onDoubleClick={() => {
                  setSelectedAbility(ability.id);
                  cast();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedAbility(ability.id);
                  }
                }}
                data-testid={`grounds-ability-item-${ability.id}`}
              >
                <div style={styles.iconWrapper}>
                  <ItemSlot
                    item={{
                      id: ability.id,
                      name: ability.name,
                      icon: iconUrl,
                    }}
                    size="sm"
                    selected={isSelected}
                  />
                </div>
                <div style={styles.abilityMetaCol}>
                  <div style={styles.abilityNameRow}>
                    <strong style={styles.abilityName}>{ability.name}</strong>
                    {ability.target && (
                      <Badge tone={targetTone(ability.target)} size="sm">
                        {ability.target}
                      </Badge>
                    )}
                  </div>
                  <span style={styles.abilityId}>{ability.id}</span>
                </div>
                <div style={styles.abilityCostCol}>
                  {abData?.manaCost ? (
                    <span style={styles.manaChip}>{abData.manaCost} MP</span>
                  ) : (
                    <span style={styles.freeChip}>FREE</span>
                  )}
                  {abData?.cooldownMs ? (
                    <span style={styles.cdChip}>
                      {(abData.cooldownMs / 1000).toFixed(1)}s
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
          {filteredAbilities.length === 0 && (
            <div style={styles.emptyList}>
              No abilities matching &ldquo;{filterQuery}&rdquo;
            </div>
          )}
        </div>

        {/* Selected Ability Facts & Stats Card */}
        {selectedAbilityData && (
          <div style={styles.factCard}>
            <div style={styles.factHeader}>
              <div style={styles.factTitleCol}>
                <span style={styles.factKicker}>Selected Ability</span>
                <strong style={styles.factTitle}>{selectedAbilityData.name}</strong>
              </div>
              <Badge tone={targetTone(selectedAbilityData.target)} size="md">
                {selectedAbilityData.target || 'neutral'}
              </Badge>
            </div>
            {selectedAbilityData?.description && (
              <p style={styles.factDesc}>{selectedAbilityData.description}</p>
            )}
            <div style={styles.factsGrid}>
              <div style={styles.factItem}>
                <span style={styles.factLabel}>Mana</span>
                <span style={styles.factVal}>
                  {detailDef?.manaCost ? `${detailDef.manaCost} MP` : '0 (Free)'}
                </span>
              </div>
              <div style={styles.factItem}>
                <span style={styles.factLabel}>Cooldown</span>
                <span style={styles.factVal}>
                  {detailDef?.cooldownMs ? `${(detailDef.cooldownMs / 1000).toFixed(1)}s` : 'Instant'}
                </span>
              </div>
              <div style={styles.factItem}>
                <span style={styles.factLabel}>Range</span>
                <span style={styles.factVal}>
                  {detailDef?.range ? `${detailDef.range}px` : 'Melee'}
                </span>
              </div>
              <div style={styles.factItem}>
                <span style={styles.factLabel}>School</span>
                <span style={styles.factVal}>
                  {detailDef?.school || 'Physical'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Free Cast Button */}
        <div style={{ marginTop: SPACE.sm }}>
          <Button
            size="md"
            variant="brass"
            fullWidth
            disabled={labState !== 'ready' || !selectedAbility || sweepRunning}
            onClick={cast}
            data-testid="grounds-cast"
          >
            Cast {selectedAbilityData?.name || 'Ability'} ▶
          </Button>
        </div>

        {/* Hotbar Segmented Assignment Row */}
        <div style={styles.hotbarSection}>
          <div style={styles.hotbarHeader}>
            <span style={styles.hotbarTitle}>Hotbar Slot Binder</span>
            <span style={styles.hotbarSub}>Quick slot assignment</span>
          </div>
          <div style={styles.slotPillsRow}>
            {HOTBAR_SLOTS.map((slotNum) => {
              const isActive = selectedSlot === (slotNum === 0 ? 10 : slotNum);
              return (
                <button
                  key={slotNum}
                  type="button"
                  style={{
                    ...styles.slotPill,
                    ...(isActive ? styles.slotPillActive : {}),
                  }}
                  onClick={() => setSelectedSlot(slotNum === 0 ? 10 : slotNum)}
                >
                  {slotNum}
                </button>
              );
            })}
          </div>
          <Button
            size="xs"
            variant="secondary"
            fullWidth
            onClick={assignToHotbar}
            data-testid="grounds-assign-hotbar"
            disabled={!selectedAbility}
          >
            Bind &ldquo;{selectedAbilityData?.name || selectedAbility}&rdquo; to Slot {selectedSlot === 10 ? '0' : selectedSlot}
          </Button>
        </div>

        {/* Action Status Feedback */}
        <div style={styles.statusBox} role="status" data-testid="grounds-last-action">
          <span style={styles.statusDot}>●</span>
          <span style={styles.statusText}>{lastAction || 'Ready for test cast.'}</span>
        </div>
      </Card>
    </div>
  );
};
